export interface MoodMap {
  "warm-optimistic": string;
  "gritty-authentic": string;
  "serene-contemplative": string;
  "energetic-dynamic": string;
  "intimate-quiet": string;
}

export type MoodKey = keyof MoodMap;

export interface TemplateDefaults {
  SUBJECT: string;
  ACTION: string;
  LOCATION?: string;
  ELEMENT: string;
  ENVIRONMENTAL_ELEMENT?: string;
  CITYSCAPE?: string;
  COLOR_PALETTE: string;
  LIGHT_DESCRIPTION: string;
  COMPOSITION_RULE: string;
  MOOD_DESCRIPTION: string;
}

export interface Template {
  name: string;
  description: string;
  setting: "outdoor" | "indoor" | "urban" | "either";
  template: string;
  defaults: TemplateDefaults;
  keywords: string[];
  moodMap: MoodMap;
}

export type TemplateKey =
  | "kinetic-athlete"
  | "atmospheric-landscape"
  | "humanist-portrait"
  | "kinetic-commute"
  | "collaborative-hub"
  | "solitary-professional";

export const TEMPLATES: Record<TemplateKey, Template> = {
  "kinetic-athlete": {
    name: "The Kinetic Athlete",
    description: "Sports, cycling, active lifestyle",
    setting: "outdoor",
    template:
      "A candid, medium-shot of [SUBJECT] mid-action, [ACTION]. Shot on Mamiya 7, 80mm lens, Kodak Portra 400. Natural, harsh afternoon sun creating sharp shadows and a lens flare. Texture of [ELEMENT] visible. High kinetic energy, unposed, documentary style. [COLOR_PALETTE]. [LIGHT_DESCRIPTION]. [COMPOSITION_RULE]. Mood: [MOOD_DESCRIPTION].",
    defaults: {
      SUBJECT: "a figure in motion",
      ACTION: "pushing through an intense physical moment",
      ELEMENT: "sweat and road grime on weathered gear",
      COLOR_PALETTE: "Earthy tones, vibrant greens and browns, sun-bleached blues",
      LIGHT_DESCRIPTION:
        "Natural harsh afternoon sun raking across the scene, creating long shadows and rim-lit highlights on sweat-dampened skin",
      COMPOSITION_RULE:
        "Dynamic diagonal composition, the subject captured mid-stride with the environment stretching behind them",
      MOOD_DESCRIPTION:
        "optimistic grit, the beauty found in physical commitment and the joy of movement",
    },
    keywords: [
      "cycling",
      "cyclist",
      "runner",
      "running",
      "athlete",
      "sport",
      "trail",
      "marathon",
      "triathlon",
      "climbing",
      "hiking",
      "biking",
      "swim",
      "fitness",
      "workout",
      "sprint",
      "race",
      "bike",
      "gravel",
      "surfing",
      "surfer",
      "kayak",
      "skateboard",
      "skating",
    ],
    moodMap: {
      "warm-optimistic":
        "golden-hour warmth radiating through physical exertion, the world glowing around them",
      "gritty-authentic":
        "raw documentary grit, sweat and dust as visual texture, unpolished and real",
      "serene-contemplative":
        "the meditative rhythm of repetitive motion, mind and body in quiet flow",
      "energetic-dynamic": "explosive kinetic energy frozen at its peak, every muscle engaged",
      "intimate-quiet":
        "a private moment of athletic focus, the world falling away around the subject",
    },
  },
  "atmospheric-landscape": {
    name: "The Atmospheric Landscape",
    description: "Travel, wide spaces, environmental",
    setting: "outdoor",
    template:
      'A wide environmental portrait of [SUBJECT] [ACTION] in a vast [LOCATION]. Golden hour "Cali light" filling the frame with a warm, hazy amber glow. Soft, organic film grain. [ELEMENT]. Shot on 120mm film, [COLOR_PALETTE]. Deep depth of field. [LIGHT_DESCRIPTION]. [COMPOSITION_RULE]. Mood: [MOOD_DESCRIPTION].',
    defaults: {
      SUBJECT: "a lone figure",
      ACTION: "standing quietly",
      LOCATION: "rolling golden landscape stretching to the horizon",
      ELEMENT:
        "Minimalist composition with a sense of immense scale, layered depth from foreground texture to distant horizon",
      COLOR_PALETTE:
        "muted but rich colors, earth tones, slate blues, and the warm cast of golden hour",
      LIGHT_DESCRIPTION:
        "Diffused directional golden-hour light with volumetric haze, the atmosphere thick with warmth",
      COMPOSITION_RULE:
        "Classical rule of thirds, placing the human figure small against the panoramic grandeur",
      MOOD_DESCRIPTION:
        "quiet, optimistic, and warm — the sublime solitude of being present in a vast world",
    },
    keywords: [
      "landscape",
      "mountain",
      "valley",
      "desert",
      "ocean",
      "coast",
      "horizon",
      "vista",
      "panorama",
      "travel",
      "wilderness",
      "field",
      "river",
      "lake",
      "sunset",
      "sunrise",
      "sky",
      "canyon",
      "forest",
      "hills",
      "plains",
      "meadow",
      "cliff",
      "beach",
      "dunes",
    ],
    moodMap: {
      "warm-optimistic": "pure golden warmth, the world bathed in possibility and gentle light",
      "gritty-authentic": "the raw, unfiltered beauty of untamed terrain under honest light",
      "serene-contemplative": "deep stillness, the quiet hum of the earth at its most peaceful",
      "energetic-dynamic":
        "the dramatic energy of shifting weather and dramatic light across the land",
      "intimate-quiet":
        "a private communion between the figure and the vast natural world around them",
    },
  },
  "humanist-portrait": {
    name: "The Humanist Portrait",
    description: "Candid portraits, stolen moments",
    setting: "either",
    template:
      "A close-up, documentary-style portrait of [SUBJECT] [ACTION]. Captured in [LOCATION]. Kodak Gold 200 film aesthetic, warm skin tones, authentic and non-posed. Shallow depth of field with a soft, blurred background. [ELEMENT]. [COLOR_PALETTE]. [LIGHT_DESCRIPTION]. [COMPOSITION_RULE]. Mood: [MOOD_DESCRIPTION].",
    defaults: {
      SUBJECT: "a weathered, expressive face",
      ACTION: "laughing genuinely, looking away from the camera",
      LOCATION: "natural dappled sunlight under a tree canopy",
      ELEMENT:
        "Rich textures in the clothing and skin, an unguarded micro-expression that reveals authentic character",
      COLOR_PALETTE:
        "Warm skin tones rendered with Portra-like fidelity, desaturated backgrounds, natural color warmth",
      LIGHT_DESCRIPTION:
        "Soft, directional window light or open shade wrapping the face with gentle modeling",
      COMPOSITION_RULE:
        "A tight crop that favors negative space on the gaze-side, drawing the viewer into the expression",
      MOOD_DESCRIPTION:
        "soulful and honest, the warmth of a stolen moment, a person fully being themselves",
    },
    keywords: [
      "portrait",
      "face",
      "person",
      "candid",
      "smile",
      "expression",
      "eyes",
      "close-up",
      "headshot",
      "barista",
      "craftsman",
      "artisan",
      "elder",
      "child",
      "stranger",
      "local",
      "vendor",
      "musician",
      "farmer",
      "laughing",
      "genuine",
    ],
    moodMap: {
      "warm-optimistic": "radiant humanity, warmth spilling from an authentic smile into the frame",
      "gritty-authentic": "honest and unvarnished, the beauty of a face shaped by lived experience",
      "serene-contemplative": "a quiet, introspective pause — eyes revealing a rich inner world",
      "energetic-dynamic":
        "the burst of genuine laughter, energy and life radiating from the subject",
      "intimate-quiet":
        "a tender, private moment, the camera an invisible witness to something real",
    },
  },
  "kinetic-commute": {
    name: "The Kinetic Commute",
    description: "Urban motion, city energy",
    setting: "urban",
    template:
      "A wide-angle street photography shot of [SUBJECT] [ACTION] through [LOCATION]. Significant motion blur on the subject to convey high velocity, while the background architecture remains sharp. Shot on 35mm Kodak Portra 800 film. [ELEMENT]. Candid, unposed, high-energy documentary style. [COLOR_PALETTE]. [LIGHT_DESCRIPTION]. [COMPOSITION_RULE]. Mood: [MOOD_DESCRIPTION].",
    defaults: {
      SUBJECT: "a figure in transit",
      ACTION: "speeding",
      LOCATION: "a sun-drenched city intersection",
      ELEMENT: "A decisive moment of urban choreography, the friction between speed and stillness",
      COLOR_PALETTE:
        "Warm golden afternoon light hitting the asphalt, deep shadows and bright hazy highlights",
      LIGHT_DESCRIPTION:
        "Mixed artificial and natural light creating a cinematic push-pull of warm and cool tones",
      COMPOSITION_RULE:
        "Leading lines from the road and architecture amplifying the sense of velocity and direction",
      MOOD_DESCRIPTION:
        "urban momentum, the beauty of city rhythm and human flow through concrete canyons",
    },
    keywords: [
      "commute",
      "messenger",
      "city",
      "urban",
      "street",
      "traffic",
      "subway",
      "bus",
      "scooter",
      "skateboard",
      "pedestrian",
      "crosswalk",
      "downtown",
      "avenue",
      "boulevard",
      "rush",
      "taxi",
      "delivery",
      "intersection",
      "speeding",
      "velocity",
    ],
    moodMap: {
      "warm-optimistic":
        "the golden promise of a city in motion, every commuter part of a beautiful choreography",
      "gritty-authentic":
        "the raw pulse of street-level urgency, asphalt and exhaust and determination",
      "serene-contemplative":
        "a slow-motion study of the urban ballet, finding calm within the chaos",
      "energetic-dynamic":
        "maximum velocity and kinetic charge, the city as an engine of human momentum",
      "intimate-quiet":
        "a solitary figure moving through the crowd, alone in the current of the city",
    },
  },
  "collaborative-hub": {
    name: "The Collaborative Hub",
    description: "Office interiors, workplace life",
    setting: "indoor",
    template:
      "An indoor lifestyle shot of [LOCATION]. In the foreground, [SUBJECT] is [ACTION], while in the background a group of people are gathered in a candid, unposed meeting. Strong natural light streaming through large industrial windows, creating long shadows and dust motes in the air. Shot on Medium Format film (Mamiya 7). [ELEMENT]. [COLOR_PALETTE]. [LIGHT_DESCRIPTION]. [COMPOSITION_RULE]. Mood: [MOOD_DESCRIPTION].",
    defaults: {
      SUBJECT: "a person walking past",
      ACTION: "a soft motion blur",
      LOCATION: "a creative office space with natural materials and warm lighting",
      ELEMENT:
        "The material textures of a working environment — wood grain, concrete, scattered papers, plants",
      COLOR_PALETTE:
        "Earthy color palette, rich textures of wood and plants, warm mid-tones and matte surfaces",
      LIGHT_DESCRIPTION:
        "Large windows providing soft directional fill while task lighting adds warm pools of amber focus",
      COMPOSITION_RULE:
        "Environmental framing that situates the subjects within the architecture of the space, layered depth",
      MOOD_DESCRIPTION:
        "optimistic and warm, the quiet energy of creative collaboration in a space that breathes",
    },
    keywords: [
      "office",
      "workspace",
      "meeting",
      "conference",
      "coworking",
      "desk",
      "team",
      "collaboration",
      "whiteboard",
      "laptop",
      "studio",
      "lab",
      "workshop",
      "brainstorm",
      "presentation",
      "startup",
      "colleagues",
      "co-working",
      "open plan",
      "creative space",
    ],
    moodMap: {
      "warm-optimistic":
        "productive warmth, sunlight-filled collaboration, the shared energy of making things together",
      "gritty-authentic":
        "the honest mess of real work — stacked papers, half-drunk coffee, ideas in progress",
      "serene-contemplative":
        "a quiet afternoon in the studio, the calm focus of deep work and creative concentration",
      "energetic-dynamic":
        "the kinetic buzz of a brainstorm in full swing, ideas bouncing off every surface",
      "intimate-quiet":
        "the gentle hum of a small team in sync, unspoken understanding in shared space",
    },
  },
  "solitary-professional": {
    name: "The Solitary Professional",
    description: "Atmospheric urban, quiet editorial",
    setting: "urban",
    template:
      'A medium shot of [SUBJECT] [ACTION] overlooking [LOCATION]. The subject is sharp, but [ELEMENT] is a long-exposure motion blur streak. Shot during the "blue hour" transition with warm interior lights glowing behind them. Kodak Gold 200 aesthetic, slight film grain, cinematic but grounded. [COLOR_PALETTE]. [LIGHT_DESCRIPTION]. [COMPOSITION_RULE]. Mood: [MOOD_DESCRIPTION].',
    defaults: {
      SUBJECT: "a young professional",
      ACTION: "leaning against a balcony railing",
      LOCATION: "a layered cityscape at dusk",
      ELEMENT: "the passing traffic or street movement below",
      COLOR_PALETTE:
        "Cool shadows and selective warm highlights, a muted tonal range with pockets of amber",
      LIGHT_DESCRIPTION:
        "A single dominant light source creating strong chiaroscuro modeling against the twilight city",
      COMPOSITION_RULE:
        "Centered or symmetrical framing that emphasizes isolation and contemplation within the urban frame",
      MOOD_DESCRIPTION:
        "a sense of quiet inertia and thoughtful observation, the poetry of solitary purpose in a crowded world",
    },
    keywords: [
      "alone",
      "solitary",
      "quiet",
      "editorial",
      "professional",
      "cafe",
      "window",
      "rain",
      "night",
      "reading",
      "thinking",
      "waiting",
      "contemplating",
      "reflection",
      "silhouette",
      "moody",
      "atmospheric",
      "balcony",
      "rooftop",
      "dusk",
      "twilight",
      "blue hour",
    ],
    moodMap: {
      "warm-optimistic":
        "a golden interior warmth contrasting with the cool dusk outside, a sense of possibility",
      "gritty-authentic":
        "the unfiltered texture of an urban evening, neon and concrete and quiet resilience",
      "serene-contemplative":
        "deep stillness at the edge of the city, watching the world slow down below",
      "energetic-dynamic":
        "the restless anticipation of nightfall, the city about to come alive again",
      "intimate-quiet":
        "a profoundly personal moment of solitude, the subject held in their own world of thought",
    },
  },
};
